import { MapPin, DollarSign, Building2, TrendingUp, Home, Layers, Landmark, Scale, Calendar, Users } from 'lucide-react';

const images = [
  'https://images.unsplash.com/photo-1606509463400-5ce6440c53e9?w=800&q=85',
  'https://images.unsplash.com/photo-1513880989635-6eb491ce7f5b?w=800&q=85',
  'https://images.unsplash.com/photo-1673262963905-9c334f7a87b3?w=800&q=85',
  'https://images.unsplash.com/photo-1730329944265-ab697e1a631c?w=800&q=85',
  'https://images.unsplash.com/photo-1557518016-95747c0eef63?w=800&q=85',
  'https://images.unsplash.com/photo-1656501375517-67129e3eed14?w=800&q=85',
  'https://images.unsplash.com/photo-1672626881472-b96d3eb5445e?w=800&q=85',
  'https://images.unsplash.com/photo-1659563159297-6f724e125026?w=800&q=85',
  'https://images.unsplash.com/photo-1697767217116-465fa27e4521?w=800&q=85',
  'https://images.unsplash.com/photo-1656501382301-9b560230018a?w=800&q=85',
];

export const westEndArticles = [
  {
    id: 'west-end-property-development-site-guide',
    category: 'Local Guide',
    title: 'Is Your West End Property a Development Site? Signs to Look For',
    excerpt: 'West End\'s inner-city lifestyle appeal and progressive zoning create significant development opportunities for landowners.',
    date: '2026-04-01',
    readTime: '7 min read',
    image: images[0],
    icon: MapPin,
    metaDescription: 'Is your West End Brisbane property a development site? Learn about zoning, development potential, and what makes West End one of Brisbane\'s most active development precincts.',
    content: `
## West End: Lifestyle Meets Development Potential

West End sits just 2km south-west of the Brisbane CBD and has evolved from a bohemian fringe suburb into one of Brisbane's most desirable inner-city addresses. Its combination of lifestyle appeal, South Bank proximity, and progressive zoning makes it one of the most active development precincts in South East Queensland.

If you own property in West End, understanding your land's development potential could reveal significantly more value than a standard residential sale.

## Key Signs Your West End Property Has Development Value

### 1. Your Zoning Allows Higher Density

West End contains a mix of residential zones under the Brisbane City Plan 2014:

| Zone | Typical Height | What Can Be Built |
|---|---|---|
| Low-Medium Density (LMR) | 2-3 storeys | Townhouses, duplexes |
| Medium Density (MDR) | 4-5 storeys | Walk-up apartments |
| High Density (HDR) | 6-12+ storeys | Apartment buildings |
| Mixed Use / Centre | 8-15+ storeys | Retail + residential, commercial |

The Montague Road corridor and areas near South Brisbane carry the highest density allowances. Properties along Boundary Street and Vulture Street also carry significant potential.

### 2. Your Block is 500m² or Larger

Standard West End lots of 400-600m² attract developer interest in MDR zones. Larger blocks of 600m²+ are highly sought, especially if they allow townhouse or apartment development.

### 3. You're Not in a Character Overlay

Parts of West End have a **Character Residential overlay** that restricts demolition of pre-1947 houses and limits building height. Properties *outside* this overlay have more straightforward development pathways and attract stronger developer demand.

### 4. You're Near South Bank or Transport

Properties within walking distance of South Bank, the Cultural Centre busway station, or key bus routes benefit from higher end-buyer demand, which translates to higher development site values.

> **Important**: West End has an active community voice regarding development. Working with an agent who understands the local planning dynamics helps navigate potential challenges and maximise value.

## What Makes West End Attractive to Developers

- **Lifestyle precinct**: Boundary Street dining, weekend markets, riverside parklands
- **South Bank adjacency**: QPAC, GOMA, State Library, Parklands — all within walking distance
- **Transport connectivity**: South Brisbane and South Bank stations, high-frequency bus routes
- **Strong end-buyer demand**: Young professionals, downsizers, and investors actively seeking West End apartments
- **Walkability score**: One of the highest in Brisbane

## How to Find Out Your Property's Potential

ACRES provides complimentary development site assessments for West End property owners. We'll evaluate your zoning, identify any character or heritage overlays, estimate development yield, and provide realistic price expectations.

## Frequently Asked Questions

**Q: Does the Character overlay prevent development in West End?**
The Character overlay restricts demolition of pre-1947 houses and limits building height, but it doesn't prevent development entirely. Properties outside the overlay have more flexibility. Some character-overlay sites can still be developed through adaptive reuse or sympathetic design.

**Q: How much more could my West End property be worth as a development site?**
Depending on zoning and constraints, West End development sites typically sell for 40-100% above standard residential value. Properties in HDR zones along Montague Road can see even greater premiums.

**Q: Are developers still buying in West End despite community opposition?**
Yes. Developer demand remains strong because end-buyer demand for West End apartments is consistently high. Experienced developers work within the planning framework and deliver projects that meet community expectations.

---

*ACRES provides free development site assessments in West End. Call 07 3096 0542 or email daniel@acres.au.*
    `,
    faq: [
      { q: 'Is my West End property a development site?', a: 'If your property is zoned MDR or higher and is not subject to a restrictive character overlay, it likely has significant development value. Even LMR-zoned sites of 600m²+ attract developer interest for townhouse projects.' },
      { q: 'Does the character overlay prevent selling to a developer in West End?', a: 'Not necessarily. While the overlay restricts demolition of pre-1947 houses, some development is still possible. Properties outside the overlay have the most straightforward development pathway.' },
      { q: 'How much more is my West End property worth as a development site?', a: 'West End development sites typically sell for 40-100% above standard residential value. HDR-zoned properties along Montague Road can command even higher premiums.' },
    ],
  },
  {
    id: 'west-end-land-prices-what-developers-pay',
    category: 'Local Guide',
    title: 'What Developers Are Paying for Land in West End',
    excerpt: 'Current development site pricing in West End and the factors driving premium land values.',
    date: '2026-04-28',
    readTime: '6 min read',
    image: images[1],
    icon: DollarSign,
    metaDescription: 'What developers are paying for land in West End Brisbane. Current development site prices, recent benchmarks, and factors driving premium land values in this inner-city suburb.',
    content: `
## West End Development Site Pricing

West End's unique combination of lifestyle appeal, inner-city location, and diverse zoning creates a nuanced development site market. Prices vary significantly based on zoning, character overlay status, and proximity to key amenities.

## Current Price Benchmarks

| Site Type | Typical Size | Price Range | Price per m² |
|---|---|---|---|
| Standard house (LMR) | 400-600m² | $900K - $1.4M | $1,800 - $2,500 |
| Standard house (MDR) | 400-600m² | $1.2M - $2.0M | $2,500 - $3,500 |
| Larger block (HDR) | 600-1,000m² | $2.0M - $4.5M | $3,000 - $5,000 |
| Montague Rd corridor | 800-2,000m² | $3.5M - $10M+ | $4,000 - $6,000 |
| Character overlay site | 400-600m² | $800K - $1.2M | $1,500 - $2,200 |

Properties outside the character overlay in MDR and HDR zones command the highest premiums. Montague Road sites with HDR or Mixed Use zoning are among the most valuable in Brisbane's inner ring.

## Key Price Drivers in West End

### Character Overlay Impact
Properties within the Character Residential overlay are worth less as development sites because demolition restrictions limit what can be built. However, they still hold value above standard residential — adaptive reuse and rear-lot development are possible on some sites.

### Lifestyle Premium
West End's established lifestyle precinct (Boundary Street, Davies Park Markets, riverside parklands) means end-buyer demand for apartments is strong. Developers can achieve premium apartment prices, which flows to higher land values.

### South Bank Proximity
Properties within walking distance of South Bank's cultural and recreational amenities attract a premium from both developers and end-buyers.

### Transport Access
Sites near South Brisbane train station, the Cultural Centre busway, and high-frequency bus routes on Montague Road and Vulture Street benefit from transit-oriented demand.

## How West End Compares

| Suburb | Avg Dev Site $/m² (MDR) | Key Advantage |
|---|---|---|
| West End | $2,500 - $3,500 | Lifestyle, South Bank adjacency |
| Woolloongabba | $2,800 - $3,800 | Cross River Rail, Olympics |
| South Brisbane | $3,500 - $5,000 | CBD proximity, limited supply |
| Kangaroo Point | $3,000 - $4,500 | River views, cliffs |

West End offers strong value relative to neighbouring suburbs, with the lifestyle premium supporting consistent developer demand.

> **Key insight**: West End properties that would sell for $850,000-$1.1M to a home buyer regularly achieve $1.3M-$2.0M+ when marketed as development sites to the right buyer pool.

## Frequently Asked Questions

**Q: Are West End development site prices still rising?**
Prices have grown steadily over the past three years. While the rate of growth may moderate, strong lifestyle demand and limited remaining supply support continued pricing strength.

**Q: Do developers pay more for sites without character overlay?**
Yes, significantly. A 600m² block in MDR without character overlay could be worth $1.5M-$2.0M, while a similar block with character overlay might only achieve $900K-$1.2M.

**Q: What's the most expensive street in West End for development sites?**
Montague Road consistently commands the highest per-square-metre rates due to HDR and Mixed Use zoning, river proximity, and strong end-buyer demand for larger apartment projects.

---

*For a confidential West End development site appraisal, contact ACRES on 07 3096 0542.*
    `,
    faq: [
      { q: 'What are developers paying for land in West End?', a: 'MDR-zoned sites sell for $2,500-$3,500 per m². HDR sites on Montague Road command $4,000-$6,000 per m². Character overlay properties sell for less at $1,500-$2,200 per m².' },
      { q: 'How does the character overlay affect West End land prices?', a: 'Properties within the character overlay are worth significantly less as development sites. A 600m² MDR block without overlay could be worth $1.5M-$2.0M vs $900K-$1.2M with the overlay.' },
      { q: 'How does West End compare to other inner-city suburbs for development site pricing?', a: 'West End offers strong value relative to Woolloongabba and South Brisbane. Its lifestyle premium supports consistent demand, with MDR sites at $2,500-$3,500 per m².' },
    ],
  },
  {
    id: 'west-end-zoning-guide-what-you-can-build',
    category: 'Local Guide',
    title: 'West End Zoning Guide: What You Can Build on Your Block',
    excerpt: 'Understanding West End\'s complex zoning landscape including character overlays, height limits, and development potential.',
    date: '2026-04-25',
    readTime: '8 min read',
    image: images[2],
    icon: Scale,
    metaDescription: 'West End Brisbane zoning guide. Understanding residential zones, character overlays, height limits, and development potential for property owners in West End.',
    content: `
## West End's Zoning Landscape

West End has one of the most complex zoning frameworks of any Brisbane suburb. The interplay between base zones, the South Brisbane Riverside Neighbourhood Plan, character overlays, and various other provisions means two properties on the same street can have dramatically different development potential.

## Base Zones in West End

### Low-Medium Density Residential (LMR)
- **Height**: 9.5m (2-3 storeys)
- **Products**: Townhouses, duplexes, secondary dwellings
- **Typical areas**: Streets east of Boundary Street, parts of Highgate Hill fringe
- **Development value**: Moderate premium for suitable sites (600m²+)

### Medium Density Residential (MDR)
- **Height**: 15m (4-5 storeys)
- **Products**: Walk-up apartments, larger townhouse developments
- **Typical areas**: Between Boundary Street and Montague Road, parts of Vulture Street
- **Development value**: Strong premium — this is the sweet spot for many West End sites

### High Density Residential (HDR)
- **Height**: 20-30m+ (6-12+ storeys, varies by Neighbourhood Plan provisions)
- **Products**: Apartment buildings
- **Typical areas**: Montague Road corridor, parts near South Brisbane boundary
- **Development value**: Highest premium — these sites command top-dollar from developers

### Mixed Use / Centre Zones
- **Height**: Up to 15+ storeys in some locations
- **Products**: Retail/commercial ground floor + residential above
- **Typical areas**: Boundary Street commercial strip, Montague Road corridor
- **Development value**: Very high — particularly where active frontage provisions allow vibrant mixed-use projects

## The Character Overlay

West End's **Character Residential overlay** covers significant portions of the suburb, particularly the traditional Queenslander-rich streets between Boundary Street and the river. Key implications:

- **Pre-1947 houses**: Cannot be demolished without specific approval (often refused)
- **New building design**: Must be sympathetic to existing character
- **Height limits**: Generally lower than base zone would otherwise allow
- **Setbacks**: More generous front and side setbacks required

| Overlay Status | Impact on Development | Impact on Value |
|---|---|---|
| No character overlay | Full development potential of base zone | Highest development premium |
| Character overlay (pre-1947 house) | Limited — renovation, raise-and-build-under, rear infill | Lower development premium |
| Character overlay (post-1947 house) | More flexibility — demolition may be possible | Moderate development premium |

## The South Brisbane Riverside Neighbourhood Plan

This neighbourhood plan provides additional detail for West End properties, including:

- **Building height maps** that override or supplement the base zone
- **Setback and boundary provisions** for specific streets
- **Active frontage requirements** along key corridors
- **View corridor protections** to and from the river
- **Public realm contributions** for larger developments

## How to Check Your West End Zoning

1. Visit [cityplan.brisbane.qld.gov.au](https://cityplan.brisbane.qld.gov.au/)
2. Search your address
3. Check **Zoning** layer for base zone
4. Check **Overlays** for character, heritage, or flood mapping
5. Check **Neighbourhood Plans** for the South Brisbane Riverside provisions

## Frequently Asked Questions

**Q: How do I know if my West End property has a character overlay?**
Check the Overlays layer on Brisbane City Plan Interactive Mapping. The Character Residential overlay is clearly marked. Properties built before 1947 within this overlay have the most restrictions.

**Q: Can I develop a character-overlay property in West End?**
Some development is possible — particularly raise-and-build-under, rear-lot infill, or adaptive reuse. However, the development potential is significantly lower than for non-overlay properties.

**Q: Is the Montague Road corridor really that much more valuable?**
Yes. Montague Road has HDR and Mixed Use zoning, river proximity, and strong transport links. Sites here can achieve $4,000-$6,000+ per m², compared to $2,500-$3,500 per m² for MDR sites elsewhere in West End.

---

*Not sure what your West End zoning means? ACRES provides free development site assessments. Call 07 3096 0542.*
    `,
    faq: [
      { q: 'What zoning does my West End property have?', a: 'West End has a complex mix of LMR, MDR, HDR, and Mixed Use zones, overlaid with the South Brisbane Riverside Neighbourhood Plan. Character overlays add further complexity. Check cityplan.brisbane.qld.gov.au for your specific zoning.' },
      { q: 'Does the character overlay prevent all development in West End?', a: 'No, but it significantly restricts options. Pre-1947 houses generally cannot be demolished. Post-1947 houses have more flexibility. Properties outside the overlay have full development potential.' },
      { q: 'Which West End streets have the highest development potential?', a: 'Montague Road has the highest density allowances and land values. Streets in MDR zones without character overlay between Boundary Street and Montague Road also offer strong development potential.' },
    ],
  },
  {
    id: 'south-bank-driving-west-end-property-values',
    category: 'Local Guide',
    title: 'How South Bank Proximity Is Driving West End Property Values',
    excerpt: 'West End\'s adjacency to South Bank is a key value driver for both developers and end-buyers.',
    date: '2026-04-22',
    readTime: '6 min read',
    image: images[3],
    icon: TrendingUp,
    metaDescription: 'How South Bank proximity drives West End property values. The lifestyle premium, transport connectivity, and cultural precinct impact on development site demand.',
    content: `
## The South Bank Advantage

West End's adjacency to South Bank — Brisbane's premier cultural, recreational, and lifestyle precinct — is one of the most powerful drivers of property value in the suburb. For development site owners, this proximity translates directly into higher land prices because developers can charge premium prices for apartments that offer South Bank on the doorstep.

## Why South Bank Matters for Property Values

### Cultural Precinct Access
South Bank is home to Queensland's most important cultural institutions:
- **Queensland Performing Arts Centre (QPAC)**
- **Gallery of Modern Art (GOMA)** and **Queensland Art Gallery**
- **Queensland Museum**
- **State Library of Queensland**

Apartment buyers pay a premium for walkable access to these world-class amenities.

### Parklands and Lifestyle
The South Bank Parklands — 17 hectares of riverside greenspace — provide an unmatched urban lifestyle. Streets Beach, the Grand Arbour, weekend markets, and riverside dining create a lifestyle that apartment buyers actively seek out.

### Transport Hub
South Bank and South Brisbane stations provide rail access, while the Cultural Centre busway is one of Brisbane's busiest transit nodes. This connectivity makes West End apartments attractive to a broad buyer pool.

### Employment Proximity
The Brisbane CBD is a short walk across the Victoria Bridge. Major employers in the precinct include the Queensland Government (1 William Street), hospitals, and universities.

## How This Affects Development Site Values

The end-buyer premium for South Bank proximity flows directly into higher development site values:

| Distance to South Bank | End-Product Premium | Land Value Impact |
|---|---|---|
| Direct adjacency (Montague Rd) | 10-15% above West End avg | Highest land values |
| 5-10 min walk | 5-10% above average | Strong premium |
| 10-15 min walk | Baseline West End pricing | Standard development premium |

Developers building apartments within a 10-minute walk of South Bank can achieve sale prices of $10,000-$14,000 per square metre for quality product — supporting land values of $3,000-$5,000+ per square metre.

## West End Streets That Benefit Most

Streets with the strongest South Bank value uplift include:
- **Montague Road** — direct river and parkland adjacency
- **Grey Street** — views to South Bank cultural precinct
- **Vulture Street** (western end) — walking distance to parklands
- **Boundary Street** (southern end) — near the Cultural Centre precinct

## The Future: West End + South Bank

Ongoing investment in South Bank ensures this value driver strengthens over time:
- Continued public domain improvements
- Expansion of cultural programming
- New dining and retail offerings
- Improved pedestrian connections to West End

> **For West End property owners**: Your proximity to South Bank is a permanent, appreciating asset. As the precinct continues to evolve, end-buyer demand — and by extension, developer demand for your land — will only grow stronger.

## Frequently Asked Questions

**Q: How much does South Bank proximity add to West End property values?**
Properties within walking distance of South Bank command a 5-15% premium over comparable West End properties further away. For development sites, this translates to higher achievable land values.

**Q: Are developers specifically targeting West End properties near South Bank?**
Yes. Developers targeting the premium apartment market actively seek sites within 10 minutes walk of South Bank. The lifestyle amenity supports higher apartment prices and stronger pre-sale results.

**Q: Will South Bank continue to grow?**
Yes. Ongoing government investment in cultural facilities, parklands, and public domain ensures South Bank remains Brisbane's premier lifestyle precinct.

---

*Own property near South Bank in West End? Contact ACRES on 07 3096 0542 for a free development site assessment.*
    `,
    faq: [
      { q: 'How does South Bank proximity affect West End property values?', a: 'Properties within walking distance of South Bank command a 5-15% premium. For development sites, this translates to higher land values as developers can charge premium apartment prices.' },
      { q: 'Which West End streets benefit most from South Bank proximity?', a: 'Montague Road, Grey Street, the western end of Vulture Street, and the southern end of Boundary Street see the strongest value uplift from South Bank adjacency.' },
      { q: 'Are developers specifically targeting sites near South Bank?', a: 'Yes. Premium apartment developers actively seek West End sites within 10 minutes walk of South Bank. The lifestyle amenity supports higher prices and stronger pre-sales.' },
    ],
  },
  {
    id: 'selling-west-end-home-to-developer-guide',
    category: 'Local Guide',
    title: 'Selling Your West End Home to a Developer: A Complete Guide',
    excerpt: 'How to navigate the unique dynamics of selling a development site in West End for maximum value.',
    date: '2026-04-18',
    readTime: '7 min read',
    image: images[4],
    icon: Home,
    metaDescription: 'How to sell your West End home to a developer. Complete guide covering character overlays, preparation, agent selection, and achieving premium development site prices.',
    content: `
## Selling in West End: Unique Considerations

Selling a development site in West End requires an understanding of the suburb's particular dynamics — character overlays, community expectations, and the premium lifestyle market. Done well, it can unlock 40-100% more value than a standard residential sale.

## Step 1: Confirm Your Development Potential

West End's complex zoning means your first step is confirming what can actually be built on your site:

- **Check your zone** (LMR, MDR, HDR, Mixed Use)
- **Check the character overlay** — this is critical in West End
- **Review the South Brisbane Riverside Neighbourhood Plan** for specific height and setback provisions
- **Check for flood overlay** — some low-lying areas near the river have flood mapping

If your property is in MDR or HDR without a character overlay, you're well positioned. If you have a character overlay, development options may be more limited but still worth investigating.

## Step 2: Choose the Right Agent

West End requires an agent who understands:
- **The character overlay implications** for your specific property
- **Community dynamics** — West End has active resident groups that influence development outcomes
- **The developer buyer pool** targeting West End
- **Premium apartment market pricing** in the area
- **Council assessment processes** for West End applications

A specialist development site agent with West End experience consistently achieves better outcomes than a generalist.

## Step 3: Prepare for the Market

### Essential preparation:
- **Boundary survey** ($2,000-$4,000)
- **Title search** and review for easements or encumbrances
- **Planning summary** confirming zone, overlay status, and development potential

### Valuable additions:
- **Town planning assessment** ($3,000-$5,000) — particularly important in West End where character and neighbourhood plan provisions add complexity
- **Concept design** ($5,000-$10,000) — shows developers what's achievable on your site

## Step 4: Market to the Right Buyers

West End attracts a specific developer profile:
- **Boutique developers** building quality 20-40 apartment projects
- **Premium operators** targeting owner-occupier buyers rather than investors
- **Mixed-use developers** interested in Boundary Street and Montague Road
- **Build-to-rent operators** seeking well-located rental housing sites

An EOI campaign targeting these specific buyer types maximises competitive tension and price.

## Step 5: Negotiate Terms

Key negotiation points for West End development site sales:
- **Price**: Benchmark against recent comparable sales in the same zone
- **Conditions**: DA conditions are standard; negotiate clear timeframes
- **Settlement**: 6-12 months is typical for conditional contracts
- **Deposit**: 5-10% — push for 10% for stronger security

## Frequently Asked Questions

**Q: Can I sell my character-overlay property to a developer?**
Yes, but the development options and price may be more limited. Some developers specialise in character-sympathetic projects. Your agent should assess whether a development sale or residential sale achieves the better outcome.

**Q: What if my neighbours are also thinking of selling?**
A coordinated sale with adjacent properties can significantly increase the combined value through site consolidation. ACRES can facilitate these discussions confidentially.

**Q: How do community groups affect the sale process in West End?**
Community input can influence DA outcomes. Experienced developers understand this and design projects that work within community expectations. A good agent will match you with developers who have strong West End track records.

---

*Ready to explore selling your West End property? Call ACRES on 07 3096 0542 for a confidential assessment.*
    `,
    faq: [
      { q: 'How do I sell my West End home to a developer?', a: 'Confirm your development potential (especially character overlay status), engage a specialist agent with West End experience, prepare with survey and planning assessment, and market through an EOI targeting boutique and premium developers.' },
      { q: 'Can I sell a character-overlay West End property to a developer?', a: 'Yes, but options and price may be more limited. Some developers specialise in character-sympathetic projects. A specialist agent will assess whether a development or residential sale achieves the better outcome.' },
      { q: 'What type of developer buys in West End?', a: 'Mostly boutique operators building quality 20-40 apartment projects for owner-occupiers. Premium developers and mixed-use operators targeting Boundary Street and Montague Road are also active.' },
    ],
  },
  {
    id: 'west-end-development-hotspots-where-developers-buy',
    category: 'Local Guide',
    title: 'West End Development Hotspots: Where Developers Want to Buy',
    excerpt: 'The specific streets and pockets within West End attracting the strongest developer demand.',
    date: '2026-04-15',
    readTime: '6 min read',
    image: images[5],
    icon: Layers,
    metaDescription: 'West End development hotspots. Which streets and areas in West End Brisbane are attracting the most developer interest? Montague Road, Boundary Street, and beyond.',
    content: `
## Where Developers Are Buying in West End

West End's development market is concentrated in specific corridors and pockets where zoning, amenity, and access align. Understanding where demand is strongest helps property owners assess their own situation.

## Hotspot 1: Montague Road Corridor

Montague Road is West End's premier development address. Running along the river, it offers:
- **HDR and Mixed Use zoning** with significant height allowances
- **River frontage** and proximity to river walks
- **Bus transport corridor** with high-frequency services
- **Strong end-buyer market** for premium apartments

Sites on Montague Road command $4,000-$6,000+ per m² — among the highest in Brisbane's inner ring.

## Hotspot 2: Boundary Street Surrounds

Properties behind and adjacent to Boundary Street's commercial strip attract developers seeking:
- **Mixed-use opportunities** with ground-floor retail or hospitality
- **Lifestyle positioning** — Boundary Street is West End's social hub
- **MDR zoning** on surrounding residential streets
- **Strong brand association** with the West End lifestyle

## Hotspot 3: Vulture Street (Western Section)

The western end of Vulture Street approaching South Brisbane carries MDR to HDR zoning:
- Transit proximity to South Brisbane station
- Dual-precinct appeal (West End lifestyle + South Bank amenity)
- Larger blocks suitable for medium-scale apartment projects

## Hotspot 4: Hardgrave Road Area

Streets around Hardgrave Road are transitioning from traditional residential to medium-density:
- MDR zoning supporting 4-5 storey developments
- Relatively affordable entry point for developers
- Good walkability to Boundary Street and South Bank

## Areas with Character Constraints

Some West End streets, while highly desirable residentially, have limited development potential due to character overlays:
- **Dornoch Terrace** — Character overlay restricts demolition
- **Forbes Street** — Traditional Queenslander streetscapes protected
- **Jane Street** — Significant character provisions

These areas may still attract developers for adaptive reuse or sympathetic infill projects, but at lower premiums than non-overlay areas.

## Developer Activity Map

| Area | Zoning | Developer Activity | Price Level |
|---|---|---|---|
| Montague Road | HDR/Mixed Use | Very High | Premium |
| Boundary Street surrounds | MDR/Centre | High | Strong |
| Vulture Street West | MDR-HDR | High | Strong |
| Hardgrave Road area | MDR | Moderate-High | Moderate |
| Character overlay streets | LMR-MDR (restricted) | Low-Moderate | Below average |

## Frequently Asked Questions

**Q: My property is on a quiet side street — do developers still want it?**
Yes, if the zoning supports development and there's no restrictive character overlay. Many developers prefer quieter streets for residential projects that appeal to owner-occupiers.

**Q: Is West End development slowing down?**
No. Developer demand remains strong, though the nature of projects is evolving. Boutique and premium developments are increasingly common as the market matures.

**Q: How do I know if my street is attracting developer interest?**
Contact ACRES for a free assessment. We track developer activity across every West End street and can advise on current demand for your specific location.

---

*Is your West End street a development hotspot? Contact ACRES on 07 3096 0542.*
    `,
    faq: [
      { q: 'Which West End streets are developers buying on?', a: 'Montague Road is the premier development address. Boundary Street surrounds, Vulture Street West, and the Hardgrave Road area also see strong developer activity. Character overlay streets see lower developer demand.' },
      { q: 'Is development activity slowing in West End?', a: 'No. Developer demand remains strong. Projects are evolving toward boutique and premium developments as the market matures and remaining sites become scarcer.' },
      { q: 'Do developers buy on quiet side streets in West End?', a: 'Yes, if zoning supports development. Many developers prefer quieter streets for residential projects targeting owner-occupiers who want the West End lifestyle without main-road noise.' },
    ],
  },
  {
    id: 'future-of-west-end-council-plans-development',
    category: 'Local Guide',
    title: 'The Future of West End: Council Plans & What They Mean for Landowners',
    excerpt: 'How evolving council policy and precinct planning are shaping West End\'s development future.',
    date: '2026-04-12',
    readTime: '6 min read',
    image: images[6],
    icon: Landmark,
    metaDescription: 'The future of West End Brisbane. How council planning, South Brisbane Riverside Neighbourhood Plan, and infrastructure investment are shaping the suburb for landowners.',
    content: `
## West End's Evolving Planning Framework

West End's planning framework continues to evolve as the suburb transitions from its traditional low-rise character to a more diverse inner-city precinct. For property owners, these changes can significantly impact your land's development potential — and its value.

## Key Planning Instruments

### South Brisbane Riverside Neighbourhood Plan
This is the primary planning document for West End, providing detailed provisions for:
- **Building heights**: Specific height maps that overlay the base zoning
- **Street setbacks**: Varied requirements by street and location
- **Character provisions**: Protections for identified character areas
- **Active frontage**: Requirements for ground-floor commercial along key streets
- **Public realm**: Contributions and design standards for larger developments

### Character Overlay
The character overlay remains a defining feature of West End's planning landscape. Council has consistently defended the overlay against challenges, signalling its ongoing importance.

### Flood Overlay
Low-lying areas near the Brisbane River and creek corridors carry flood mapping that affects:
- Minimum floor levels for habitable rooms
- Reduced ground-floor development potential
- Additional construction costs for flood-resilient design

## What's Changing

### Density Increases Along Corridors
Council continues to focus density increases along major transport and commercial corridors (Montague Road, Vulture Street, Boundary Street) while maintaining lower densities on quieter residential streets. This approach means:

- Properties on or near corridors are gaining development potential
- Properties in character areas are likely to retain current restrictions
- The gap in value between corridor and residential sites is widening

### Mixed-Use Transition
Boundary Street is increasingly being planned as a mixed-use corridor rather than a purely commercial strip. This creates development opportunities for properties with commercial or mixed-use zoning.

### Green Infrastructure
Council is investing in green infrastructure — urban tree planting, stormwater management, and public parks — that enhances the precinct's lifestyle appeal and supports property values.

## What This Means for Property Owners

| Your Property Location | Likely Future Trajectory | Action to Consider |
|---|---|---|
| Montague Road corridor | Further density increases possible | Strong position — consider timing |
| MDR zone, no overlay | Stable to increasing density | Current market is favourable for sellers |
| Character overlay area | Likely to remain restricted | Assess adaptive reuse or residential sale |
| Flood-affected area | Additional construction requirements | Factor into expectations |

## Frequently Asked Questions

**Q: Will council remove the character overlay in West End?**
Unlikely in the foreseeable future. Council has consistently supported the overlay and community sentiment strongly favours its retention. Properties outside the overlay are better positioned for development.

**Q: Could my property be upzoned in the future?**
Properties along major corridors, near transport nodes, and in areas identified for transition may be upzoned in future City Plan reviews. However, this is uncertain and shouldn't be relied upon.

**Q: How do planning changes affect my property's value?**
Density increases generally raise development site values. Character restrictions maintain values at current levels. Flood overlay can reduce development potential and value. A specialist agent can assess the impact for your specific property.

---

*Understand how planning changes affect your West End property. Contact ACRES on 07 3096 0542.*
    `,
    faq: [
      { q: 'Will the West End character overlay be removed?', a: 'Unlikely. Council has consistently supported the overlay and community sentiment strongly favours retention. Properties outside the overlay have the most development potential.' },
      { q: 'Could my West End property be upzoned?', a: 'Properties along corridors and near transport may be upzoned in future City Plan reviews, but this is uncertain. Current zoning determines present-day development value.' },
      { q: 'How do West End planning changes affect property values?', a: 'Density increases along corridors raise development site values. Character restrictions maintain current levels. A specialist agent can assess the specific impact on your property.' },
    ],
  },
  {
    id: 'west-end-corner-blocks-large-lots-premiums',
    category: 'Local Guide',
    title: 'West End Corner Blocks & Large Lots: Why Developers Pay More',
    excerpt: 'Corner blocks and larger lots in West End command significant premiums from developer buyers.',
    date: '2026-04-08',
    readTime: '5 min read',
    image: images[7],
    icon: Building2,
    metaDescription: 'Why developers pay more for corner blocks and large lots in West End Brisbane. Premium pricing, design advantages, and what makes your West End block attractive.',
    content: `
## Corner Block and Large Lot Premiums in West End

In a suburb where remaining development sites are increasingly scarce, corner blocks and large lots command significant premiums. Developers compete aggressively for these properties because they offer design, yield, and market advantages that translate to better project outcomes.

## Why Corners Are Worth More in West End

### Design Advantages
- **Dual aspect apartments** with cross-ventilation and natural light from two street frontages
- **Separate access** — pedestrian entry from one street, vehicle entry from the other
- **Better streetscape engagement** — buildings that address both streets achieve higher architectural quality
- **Reduced single-aspect units** — fewer south-facing or poorly lit apartments

### Planning Benefits
The Brisbane City Plan sometimes provides additional allowances for corner sites:
- Increased site coverage
- More generous setback provisions
- Additional height where both street frontages are addressed

### Market Advantages
Corner apartments in completed projects sell for a premium:
- Better views and outlook from multiple orientations
- Greater perceived privacy (not flanked by neighbouring buildings on two sides)
- More desirable building addresses

## What Corner Blocks Are Worth in West End

| Zone | Mid-Block 600m² | Corner 600m² | Premium |
|---|---|---|---|
| LMR | $900K - $1.2M | $1.1M - $1.5M | 20-25% |
| MDR | $1.4M - $1.8M | $1.7M - $2.4M | 25-35% |
| HDR | $2.0M - $3.0M | $2.5M - $4.0M | 25-35% |

## Large Lots: Rare and Valuable

Large lots (700m²+) in West End are scarce and command significant premiums:
- **Higher development yield** on a single title
- **No amalgamation risk** — the developer doesn't need to negotiate with neighbours
- **More efficient building design** possible on larger footprints
- **Faster project commencement** — single-title acquisition and approval

Properties of 800m²+ in MDR or HDR zones without character overlay are among the most valuable development sites in West End.

## Site Consolidation Opportunities

If you own property adjacent to another potential development site, coordinated sale can increase total value by 10-20% through site consolidation. ACRES can facilitate confidential discussions with neighbours to explore this option.

## Frequently Asked Questions

**Q: How much more is a corner block worth in West End?**
Typically 20-35% more than a comparable mid-block site in the same zone. In MDR, a 600m² corner might achieve $1.7M-$2.4M versus $1.4M-$1.8M for a standard block.

**Q: I have a large block with a character overlay — does the premium still apply?**
The size premium still exists but is moderated by the character restrictions. A specialist agent can assess whether the combined factors support a development sale or a standard residential sale.

**Q: Can I coordinate a sale with my neighbour?**
Yes. ACRES regularly facilitates confidential multi-vendor sales. The combined value is typically higher than individual sales, and the process can be managed to protect both parties' interests.

---

*Own a corner block or large lot in West End? Contact ACRES on 07 3096 0542 for a premium assessment.*
    `,
    faq: [
      { q: 'How much more is a corner block worth in West End?', a: 'Corner blocks typically command 20-35% more than mid-block sites. A 600m² MDR corner might achieve $1.7M-$2.4M versus $1.4M-$1.8M for a standard block.' },
      { q: 'Are large lots in West End particularly valuable?', a: 'Yes. Lots of 700m²+ are rare in West End and command significant premiums. Properties of 800m²+ in MDR or HDR without character overlay are among the most valuable development sites in the suburb.' },
      { q: 'Can I sell my West End property together with my neighbour?', a: 'Yes. ACRES facilitates confidential multi-vendor sales for site consolidation. The combined value is typically 10-20% higher than individual sales.' },
    ],
  },
  {
    id: 'west-end-property-market-2025-development-demand',
    category: 'Local Guide',
    title: 'West End Property Market 2026: Development Site Demand & Pricing',
    excerpt: 'Current market conditions for West End development sites and where the market is heading.',
    date: '2026-04-05',
    readTime: '6 min read',
    image: images[8],
    icon: Calendar,
    metaDescription: 'West End Brisbane property market 2025. Development site demand, pricing trends, buyer activity, and market outlook for sellers in this premium inner-city suburb.',
    content: `
## Market Overview: West End 2025

West End's development site market in 2026 is characterised by strong demand, limited supply, and evolving buyer profiles. The suburb's maturation from an emerging development precinct to an established premium address is reflected in both site pricing and the quality of projects being delivered.

## Current Demand Profile

### Who's Buying
- **Boutique developers**: Quality-focused operators building 15-40 apartment projects
- **Premium operators**: Targeting owner-occupier buyers with high-end finishes and design
- **Mixed-use developers**: Interested in Boundary Street and Montague Road opportunities
- **Build-to-rent operators**: Seeking well-located rental housing sites in established lifestyle precincts
- **National developers**: Occasional interest in larger amalgamated sites

### Market Characteristics
- **Demand exceeds supply**: More active buyers than available sites
- **Off-market transactions increasing**: Many sales occurring through private networks before public marketing
- **Quality focus**: Developers competing on design and amenity, not just price
- **Community-aware buyers**: Developers who understand West End's character expectations

## Pricing Trends

| Year | MDR $/m² Range | HDR $/m² Range |
|---|---|---|
| 2022 | $2,000 - $2,800 | $2,800 - $4,000 |
| 2023 | $2,200 - $3,000 | $3,000 - $4,500 |
| 2024 | $2,400 - $3,200 | $3,200 - $5,000 |
| 2025 (current) | $2,500 - $3,500 | $3,500 - $6,000 |

Growth has been steady rather than explosive, reflecting the suburb's maturity and the quality-focused nature of the buyer pool.

## Supply Dynamics

Available development sites in West End are becoming scarce:
- Character overlay areas are effectively off-limits for conventional development
- Many non-overlay sites have already been developed or acquired
- Remaining opportunities are concentrated along corridors and in transition zones
- Site consolidation is increasingly necessary for larger projects

This supply constraint supports ongoing pricing strength.

## Outlook for Sellers

### Positive Factors
- Sustained lifestyle demand from end-buyers
- Limited remaining development sites
- South Bank precinct continues to enhance
- Strong rental market supporting build-to-rent demand

### Considerations
- Character overlay limits some sites
- Community expectations influence DA outcomes
- Construction costs affect developer feasibility
- Competition from other inner-city suburbs

> **Bottom line**: West End remains one of Brisbane's strongest development site markets. Current conditions are favourable for sellers, and the limited remaining supply suggests prices will remain firm.

## Frequently Asked Questions

**Q: Is 2025 a good time to sell a development site in West End?**
Yes. Demand exceeds supply, pricing is firm, and the lifestyle fundamentals that drive West End's appeal continue to strengthen. Current conditions are favourable for achieving premium outcomes.

**Q: Will West End development site prices keep rising?**
Growth is expected to continue, though at a moderate pace. The limited remaining supply and sustained end-buyer demand provide a strong floor for pricing.

**Q: How do I compete with off-market transactions in West End?**
Engage a specialist agent with strong developer relationships who can position your property for both off-market and marketed sale, depending on which approach maximises your outcome.

---

*For a current West End market assessment, contact ACRES on 07 3096 0542.*
    `,
    faq: [
      { q: 'Is 2025 a good time to sell a development site in West End?', a: 'Yes. Demand exceeds supply, pricing is firm, and lifestyle fundamentals continue to strengthen. Conditions are favourable for achieving premium development site outcomes.' },
      { q: 'Are West End development site prices still rising?', a: 'Growth continues at a moderate pace. Limited remaining supply and sustained end-buyer demand provide a strong floor for pricing. Steady rather than explosive growth reflects the suburb maturity.' },
      { q: 'What type of developer is most active in West End?', a: 'Boutique and premium developers building 15-40 apartment projects for owner-occupiers. Mixed-use developers targeting Boundary Street and build-to-rent operators are also increasingly active.' },
    ],
  },
  {
    id: 'downsizing-west-end-selling-developer-retirement',
    category: 'Local Guide',
    title: 'Downsizing in West End: How Selling to a Developer Funds Your Next Chapter',
    excerpt: 'Long-term West End homeowners could unlock significant value by selling to a developer rather than a home buyer.',
    date: '2026-04-01',
    readTime: '6 min read',
    image: images[9],
    icon: Users,
    metaDescription: 'Downsizing in West End Brisbane by selling to a developer. How long-term homeowners can unlock premium development value to fund retirement and their next chapter.',
    content: `
## Your West End Home Could Be Worth More Than You Think

If you've lived in West End for many years, you've watched the suburb transform from a quiet, affordable neighbourhood into one of Brisbane's most sought-after lifestyle precincts. That transformation has created enormous value — particularly for properties with development potential.

Selling to a developer, rather than a home buyer, could unlock 40-100% more value from your property.

## The West End Downsizer Opportunity

### The Numbers
A typical West End downsizing scenario:

| Factor | Standard Sale | Developer Sale |
|---|---|---|
| Property | 3-bed Queenslander on 600m² (MDR, no overlay) | Same property |
| Buyer type | Home buyer / renovator | Apartment developer |
| Sale price | $1,100,000 | $1,700,000 |
| Agent commission | $22,000 | $34,000 |
| Net proceeds | $1,078,000 | $1,666,000 |
| **Additional value** | — | **$588,000** |

### Tax Benefits
If the property has been your principal residence, the capital gain is generally **exempt from CGT**. Combined with the Downsizer Contribution (up to $300,000 per person into super), a development site sale can significantly strengthen your retirement position.

## Where the Premium Comes From

Developers are willing to pay more because they're buying your land's *future potential*, not its current use:
- A 600m² MDR block could support 12-16 apartments
- Those apartments could generate $8M-$12M in total sales
- After construction and other costs, the developer needs a margin
- What's left is still substantially more than a home buyer would pay

## Common Concerns for Long-Term Owners

### "But I love West End — I don't want to leave"
You don't have to leave the suburb. Many downsizers purchase an apartment in a nearby completed development. You can stay in West End while upgrading to a brand-new, low-maintenance home — and bank the difference.

### "The character overlay means my house can't be demolished"
If your property has a character overlay and a pre-1947 house, development options are more limited. However, some development may still be possible, and a specialist agent can assess whether a development sale or residential sale is the better option.

### "I don't want my neighbours to know I'm selling"
Off-market sales are common in West End. Your agent can approach developers confidentially without any public marketing.

### "The process sounds complicated"
It is more complex than a standard house sale, but a specialist agent manages the entire process. You continue living in your home throughout marketing and the conditional period.

## Your Next Steps

1. **Get informed**: Request a free, confidential development site assessment from ACRES
2. **Understand your options**: Compare development sale value vs standard residential value
3. **Take your time**: There's no rush — understand the opportunity before making any decisions
4. **Plan your next chapter**: Where will you live? What will you do with the financial freedom?

## Frequently Asked Questions

**Q: Can I stay in West End after selling to a developer?**
Yes. Many downsizers purchase an apartment in a nearby development and stay in the community they love, while banking the significant financial difference.

**Q: What if my West End property has a character overlay?**
Character overlay properties have limited development potential but may still command a premium through adaptive reuse. A specialist agent will assess whether a development or residential sale achieves the better outcome.

**Q: How long before I need to move?**
Development site contracts typically include extended settlement of 6-18 months. You have plenty of time to plan your next move.

---

*Considering downsizing from your West End home? Call ACRES on 07 3096 0542 for a confidential assessment.*
    `,
    faq: [
      { q: 'How much more could I get selling my West End home to a developer?', a: 'Properties in MDR zones without character overlay typically sell for 40-100% above residential value. A home worth $1.1M could achieve $1.5M-$2.0M+ from a developer.' },
      { q: 'Can I stay in West End after selling to a developer?', a: 'Yes. Many downsizers purchase an apartment in a nearby development and stay in the community while banking the significant financial difference.' },
      { q: 'What about the character overlay on my West End property?', a: 'Character overlay properties have limited development potential. A specialist agent will assess whether the development premium or standard residential value achieves the better outcome for your situation.' },
    ],
  },
];
